import { useEffect, useRef, useState, type ReactNode } from "react";

import type { ClubView, NewsRow } from "../game/types";
import { noticiaDoMotor, useT, type Chave } from "../i18n";
import { CartaoCompartilhavel } from "../ui/CartaoCompartilhavel";
import type { DadosDeCartao } from "../ui/cartoes";
import { Icone } from "../ui/icons";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { cartaoDaContratacao } from "./panels";

/**
 * O sino das notificações da carreira.
 *
 * É o que a aba Notícias foi até aqui: a proposta que chegou, o garoto da base,
 * a diretoria, o título. Virou sino porque é aviso, e aviso se olha de relance,
 * de qualquer tela, sem trocar de seção. A aba Notícias ficou para o jornal do
 * mundo.
 *
 * Abrir marca tudo como lido no save. As bolinhas de não lida continuam na
 * lista aberta, pela foto tirada ao abrir: some o contador, não a pista de
 * onde está a novidade.
 */

type Aba = "mercado" | "elenco" | "diretoria" | "competicao";

const ABAS: Array<{ aba: Aba; tipos: string[] }> = [
  { aba: "mercado", tipos: ["mercado"] },
  { aba: "elenco", tipos: ["elenco", "lesao"] },
  { aba: "diretoria", tipos: ["diretoria", "financeiro"] },
  { aba: "competicao", tipos: ["competicao", "resultado"] },
];

function abaDo(item: NewsRow): Aba {
  return ABAS.find(({ tipos }) => tipos.includes(item.kind))?.aba ?? "competicao";
}

export function SinoDeNotificacoes({ view, onAbrir }: { view: ClubView; onAbrir: () => void }): ReactNode {
  const t = useT();
  const [aberto, definirAberto] = useState(false);
  const [aba, definirAba] = useState<Aba>("mercado");
  const [naoLidasAoAbrir, definirNaoLidasAoAbrir] = useState<Set<string>>(new Set());
  const [cartao, definirCartao] = useState<DadosDeCartao | null>(null);
  const raiz = useRef<HTMLDivElement>(null);

  const naoLidas = view.news.filter((item) => !item.read);

  function abrir(): void {
    const ids = new Set(naoLidas.map((item) => item.id));
    definirNaoLidasAoAbrir(ids);
    // Começa na aba que tem novidade, e não sempre no mercado.
    const primeira = ABAS.find(({ aba: a }) => naoLidas.some((item) => abaDo(item) === a));
    if (primeira) definirAba(primeira.aba);
    definirAberto(true);
    if (ids.size > 0) onAbrir();
  }

  // Clique fora fecha, como qualquer menu.
  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: PointerEvent): void => {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) definirAberto(false);
    };
    window.addEventListener("pointerdown", fora);
    return () => window.removeEventListener("pointerdown", fora);
  }, [aberto]);

  const lista = view.news.filter((item) => abaDo(item) === aba);

  return (
    <div className="sino-raiz" ref={raiz}>
      <button
        type="button"
        className="ghost sino-botao"
        aria-label={t("sino.titulo")}
        aria-expanded={aberto}
        onClick={() => (aberto ? definirAberto(false) : abrir())}
      >
        <Icone nome="Sino" size={18} />
        {naoLidas.length > 0 && <span className="sino-conta num">{naoLidas.length > 99 ? "99+" : naoLidas.length}</span>}
      </button>

      {aberto && (
        <Gaveta aoFechar={() => definirAberto(false)}>
          <div className="sino-topo">
            <b>{t("sino.titulo")}</b>
          </div>
          <div className="sino-abas" role="tablist">
            {ABAS.map(({ aba: a }) => {
              const novas = view.news.filter((item) => abaDo(item) === a && naoLidasAoAbrir.has(item.id)).length;
              return (
                <button type="button" role="tab" key={a} aria-selected={aba === a} onClick={() => definirAba(a)}>
                  {t(`sino.abas.${a}` as Chave)}
                  {novas > 0 && <span className="sino-n num">{novas}</span>}
                </button>
              );
            })}
          </div>
          <div className="sino-lista">
            {lista.length === 0 ? (
              <p className="muted" style={{ margin: 0, padding: "16px 14px", fontSize: 12.5 }}>
                {t("sino.vazio")}
              </p>
            ) : (
              lista.map((item) => {
                const escrita = item.key === null ? null : noticiaDoMotor(item.key, item.params);
                const doCartao = cartaoDaContratacao(view, item);
                return (
                  <div className="sino-item" key={item.id} data-nova={naoLidasAoAbrir.has(item.id) ? "" : undefined}>
                    <span className="sino-bolinha" />
                    <div style={{ minWidth: 0 }}>
                      <b>{escrita?.titulo ?? item.title}</b>
                      <span>{escrita?.corpo ?? item.body}</span>
                      {doCartao && (
                        <button type="button" className="ghost sino-cartao" onClick={() => definirCartao(doCartao)}>
                          {t("noticias.compartilharContratacao")}
                        </button>
                      )}
                    </div>
                    <small className="num">{t("noticias.rodada", { rodada: item.round })}</small>
                  </div>
                );
              })
            )}
          </div>
        </Gaveta>
      )}

      {cartao && <CartaoCompartilhavel dados={cartao} aoFechar={() => definirCartao(null)} />}
    </div>
  );
}

/** A caixa aberta do sino, com o Esc fechando como nos outros diálogos. */
function Gaveta({ aoFechar, children }: { aoFechar: () => void; children: ReactNode }): ReactNode {
  useDialogoDeAtalhos({ nome: "Notificações", nivel: 20, aoFechar });
  return (
    <div className="sino-gaveta" role="dialog">
      {children}
    </div>
  );
}
